// Producer contract / DocuSign hooks.
//
// One contract per (project, character). The "Send for signing" button posts
// to `/api/v1/contracts/:id/send` which returns a signed envelope link the
// server has already routed through DocuSign.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type ListEnvelope, api, unwrapItems } from '../api'
import type { ContractRow, PickupMode } from './producer-types'

export const contractsKey = (projectId: string) => ['contracts', projectId] as const
export const contractKey = (contractId: string) => ['contract', contractId] as const

export function useContracts(projectId: string | undefined) {
  return useQuery({
    queryKey: projectId ? contractsKey(projectId) : ['contracts', '__none__'],
    enabled: Boolean(projectId),
    queryFn: () =>
      api<ListEnvelope<ContractRow>>(`/api/v1/projects/${projectId}/contracts`).then(unwrapItems),
  })
}

export interface UpsertContractTermsInput {
  contract_id: string
  line_rate_usd: number
  pickup_mode: PickupMode
  pickup_rate_usd: number
}

/** Save rate / pickup terms before sending to DocuSign. Optimistic. */
export function useUpsertContractTerms(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ contract_id, ...body }: UpsertContractTermsInput) =>
      api<ContractRow>(`/api/v1/contracts/${contract_id}/terms`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onMutate: async (input) => {
      const key = contractsKey(projectId)
      await qc.cancelQueries({ queryKey: key })
      const prev = qc.getQueryData<ContractRow[]>(key)
      if (prev) {
        qc.setQueryData<ContractRow[]>(
          key,
          prev.map((row) =>
            row.id === input.contract_id ? { ...row, /* terms preview only */ ...row } : row,
          ),
        )
      }
      return { prev }
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) qc.setQueryData(contractsKey(projectId), ctx.prev)
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: contractsKey(projectId) })
    },
  })
}

/** Trigger DocuSign envelope creation for one contract. */
export function useSendContractForSigning(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (contractId: string) =>
      api<ContractRow>(`/api/v1/contracts/${contractId}/send`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: contractsKey(projectId) })
    },
  })
}

/** Trigger Stripe Connect transfer for one (or many) approved batches. */
export function useTriggerPayouts(projectId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { batch_ids: string[] }) =>
      api<{ payout_count: number; total_usd: number }>(`/api/v1/projects/${projectId}/payouts`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['contracts', projectId] })
      void qc.invalidateQueries({ queryKey: ['milestones', projectId] })
    },
  })
}
